import React, { useState, useEffect } from 'react';
import {
  DataTableV2, DataTableBodyV2, DataTableHeadV2,
  DataTableHeadCellV2, DataTableRowV2, DataTableCellV2,
  DataTableExpandableContentV2, IconEdit, IconTrash, IconPlay,
} from '@americanexpress/dls-react';
import { parseHtml } from '../../utils/util';

const getVal = require('lodash/get');

const ExpandableTable = (childProps) => {
  const [tableData, setTableData] = useState([]);
  const {
    tableDynamicProperties,
    fieldLabel,
    entityData = {},
    operationCaller,
    showId,
    fieldId,
    cssProperties,
    entityComments,
    expandableContent = {},
    rowExpansionCondition = null,
    setFormSubmissionPayload,
    formSubmissionPayload,
    isTableLengthRequired = false,
    isScrollTopOnData = false,
    setEntityCompositeKey,
    scrollToClass = '',
    keysToUpdate = {},
    extractMapResponseValueByKey,
  } = childProps;

  // Initialize the expand state of rows
  const [isRowExpanded, setIsRowExpanded] = useState([]);
  const tableLabelCss = fieldLabel.cssProperties.tableLabelCss ? fieldLabel.cssProperties.tableLabelCss : 'label-2';
  const noRecordLabelCss = fieldLabel.cssProperties.noRecordLabelCss ? fieldLabel.cssProperties.noRecordLabelCss : 'margin-1-t label-4';
  const additionalDataMsgCss = fieldLabel.cssProperties.additionalDataMsgCss ? fieldLabel.cssProperties.additionalDataMsgCss : 'margin-1-t label-4';

  /* eslint-disable max-len -- need */
  const updateExpandedRowState = (data) => {
    if (rowExpansionCondition && data && data.length > 0) {
      const newRowStates = [];
      data.forEach((row, index) => {
        let needToExpandThisRow = false;
        if (rowExpansionCondition.type) {
          switch (rowExpansionCondition.type) {
            case 'mapValue':
              if (getVal(row, rowExpansionCondition.selectKey) === rowExpansionCondition.valueToMap) {
                needToExpandThisRow = true;
              }
              break;
            case 'dynamicMapping':
              needToExpandThisRow = extractMapResponseValueByKey(rowExpansionCondition, 'dynamicMapping', row);
              break;
            default: needToExpandThisRow = false;
          }
        }
        if (needToExpandThisRow) {
          newRowStates[index] = false;
        }
      });
      setIsRowExpanded([...newRowStates]);
    }
    return null;
  };

  // Whenever change to the entityData, initialise new table data
  useEffect(() => {
    if (showId && entityData[showId]) {
      updateExpandedRowState(entityData[showId]);
      setTableData([...entityData[showId]]);
    } else {
      setTableData([]);
    }
  }, [entityData, showId]);

  useEffect(() => {
    // update the number of records present
    if (isTableLengthRequired) {
      formSubmissionPayload[fieldId] = tableData.length;
      formSubmissionPayload.elemId = null;
      setFormSubmissionPayload({ ...formSubmissionPayload });
    }
    if (tableData.length > 0) {
      if (scrollToClass !== '') {
        const selector = document.querySelector(`.${scrollToClass}`);
        if (selector) {
          selector.scrollIntoView({
            behavior: 'smooth',
          });
        }
      } else if (isScrollTopOnData) {
        window.scrollTo(0, 0);
      }
    }
  }, [tableData]);

  const handleEditEntity = (index, showIdVal, compositeKey) => {
    setEntityCompositeKey(compositeKey);
    operationCaller([{ type: 'editEntity' }], { index, entity: showIdVal });
    if (Object.keys(keysToUpdate)) {
      setFormSubmissionPayload({ ...formSubmissionPayload, ...keysToUpdate });
    }
  };

  // to get values to display inside row cells
  /* eslint-disable-next-line max-params -- desc */
  const displayRequestKeyElement = (reqKey, data, index, compositeKey) => {
    switch (reqKey) {
      case 'editAndRemoveButton':
        return (
          <>
            <IconEdit
              id={`edit${index}`}
              onClick={() => handleEditEntity(index, showId, compositeKey)}
              size="sm"
              className="margin-1 dls-deep-blue"
            >
              {fieldLabel.editEntityButtonLabel}
            </IconEdit>
            <IconTrash
              id={`remove${index}`}
              onClick={() => operationCaller([{ type: 'removeEntity' }], { index, entity: showId, keysToUpdate })}
              size="sm"
              className="margin-1 dls-red"
            >
              {fieldLabel.removeEntityButtonLable}
            </IconTrash>
          </>
        );
      case 'removeButton':
        return (
          <IconTrash
            id={`remove${index}`}
            onClick={() => operationCaller([{ type: 'removeEntity' }], { index, entity: showId })}
            size="sm"
            className="margin-1 dls-red icon-hover"
          >
            {fieldLabel.removeEntityButtonLable}
          </IconTrash>
        );
      case 'startButton':
        return (
          <IconPlay
            id={`edit${index}`}
            onClick={() => handleEditEntity(index, showId, compositeKey)}
            size="sm"
            className="margin-1 dls-deep-blue"
          >
            {fieldLabel.editEntityButtonLabel}
          </IconPlay>
        );
      case 'editButton':
        return (
          <IconEdit
            id={`edit${index}`}
            onClick={() => handleEditEntity(index, showId, compositeKey)}
            size="sm"
            className="margin-1 dls-deep-blue"
          >
            {fieldLabel.editEntityButtonLabel}
          </IconEdit>
        );
      default:
      {
        const val = getVal(data, reqKey);
        return val;
      }
    }
  };

  // toggle row expansion
  const handleExpandRowClick = (e, { id }) => {
    const newArr = [...isRowExpanded];
    const indx = id - 1;
    newArr[indx] = !newArr[indx];
    setIsRowExpanded(newArr);
  };

  const getBodyJsx = (body, data) => {
    const bodyJsx = [];
    body.forEach((element) => {
      // decide to show this element or not and which element to show
      let elementJsx = null;
      switch (element.elementType) {
        case 'keyValue':
          {
            const val1 = getVal(data, element.selectKey);
            const val = val1 && val1 !== '' ? val1.trim() : null;
            if (val) {
              elementJsx = (
                <p className={element.elementCss}>
                  {parseHtml(element.elementLabel)}
                  {getVal(data, element.selectKey)}
                </p>
              );
            }
          }
          break;
        case 'paragraph':
          {
            let visible = false;
            switch (element.visibilityCondition.type) {
              case 'isKeyPresent':
                {
                  const val1 = getVal(data, element.visibilityCondition.selectKey);
                  const val = val1 && val1 !== '' ? val1.trim() : null;
                  if (val) {
                    visible = true;
                  }
                }
                break;
              default: visible = false;
            }
            if (visible) {
              elementJsx = (
                <p className={element.elementCss}>
                  {element.elementLabel}
                </p>
              );
            }
          }
          break;
        case 'fileAttachment':
          {
            const files = getVal(data, element.selectKey) ? getVal(data, element.selectKey) : [];
            if (files.length > 0) {
              const fileNamesJsx = [];
              files.forEach((fileObj, index) => {
                const nameJsx = (
                  <span className={element.fileNameCss}>
                    {`  File${index + 1}- ${fileObj.name}`}
                  </span>
                );
                fileNamesJsx.push(nameJsx);
              });

              elementJsx = (
                <p className={element.elementCss}>
                  {element.elementLabel}{fileNamesJsx}
                </p>
              );
            }
          }
          break;
        default: elementJsx = null;
      }
      // Add element to body JSX
      if (elementJsx) {
        bodyJsx.push(elementJsx);
      }
    });
    return bodyJsx;
  };

  const getDynamicContent = (data) => {
    // create expandable content JSX
    const dynamicContentJsxArray = [];
    expandableContent.sections.forEach((section) => {
      // decide if section is visible or not
      let isSectionVisible = false;
      switch (section.visibilityCondition.type) {
        case 'isKeyPresent':
          {
            const val1 = getVal(data, section.visibilityCondition.selectKey);
            const val = val1 && val1 !== '' ? val1.trim() : null;
            if (val) {
              isSectionVisible = true;
            }
          }
          break;
        default: isSectionVisible = false;
      }
      if (isSectionVisible) {
        const sectionJsx = (
          <div className={section.sectionCss}>
            {
              getBodyJsx(section.sectionBodyElements, data)
            }
          </div>
        );
        dynamicContentJsxArray.push(sectionJsx);
      }
    });
    return (
      dynamicContentJsxArray && dynamicContentJsxArray.length > 0
        ? (
          <div className={expandableContent.css}>
            {
              dynamicContentJsxArray
            }
          </div>
        )
        : null
    );
  };
  /* eslint-enable max-len -- need */
  return (
    <div className={cssProperties.style}>
      {
        fieldLabel && fieldLabel.label
        && (
          <p className={tableLabelCss}>
            {parseHtml(fieldLabel.label)}
          </p>
        )
      }
      {
        tableData.length > 0
        && (
          <DataTableV2
            bordered={true}
            id="table-base-instance"
            striped={true}
            className="display-block-sm-down scroll-x-sm-down"
            expandable={isRowExpanded && isRowExpanded.length > 0}
            onExpandRowClick={handleExpandRowClick}
            style={tableDynamicProperties.isTableScrollable
              ? { display: 'block', 'max-height': `${tableDynamicProperties.maxHeight}`, 'overflow-y': 'scroll' }
              : null}
          >
            <DataTableHeadV2>
              <DataTableRowV2>
                {
                  tableDynamicProperties.displayHeaders.map((item, index) => (
                    <DataTableHeadCellV2
                      key={item}
                      className={tableDynamicProperties.displayHeadersCss
                        ? tableDynamicProperties.displayHeadersCss[index]
                        : null}
                    >
                      {item}
                    </DataTableHeadCellV2>

                  ))
                }
              </DataTableRowV2>
            </DataTableHeadV2>
            <DataTableBodyV2>

              {
                tableData.map((data, index) => (
                  <DataTableRowV2 rowIndex={index + 1} key={`row-${index + 1}`} id={index + 1} expanded={isRowExpanded[index]} expandButtonLabel={`expand-${index + 1}`}>
                    {
                      tableDynamicProperties.valueKeys.map((reqKey, i) => (
                        <DataTableCellV2
                          colIndex={i + 1}
                          key={reqKey}
                          className={tableDynamicProperties.displayRowsCss
                            ? tableDynamicProperties.displayRowsCss[i]
                            : null}
                          style={{
                            border: entityComments.length > 0
                              && Object.keys(entityComments.filter(
                                (obj) => obj.compositeKey === data.compositeKey)).length > 0
                              && Object.keys(entityComments.filter(
                                (obj) => obj.compositeKey === data.compositeKey)[0]
                              ).includes(reqKey) ? '.0625rem solid red' : '',
                            color: entityComments.length > 0
                              && Object.keys(entityComments.filter(
                                (obj) => obj.compositeKey === data.compositeKey)
                              ).length > 0
                              && Object.keys(entityComments.filter(
                                (obj) => obj.compositeKey === data.compositeKey
                              )[0]).includes(reqKey) ? 'red' : '',
                          }}
                        >
                          {
                            displayRequestKeyElement(reqKey, data, index, data.compositeKey)
                          }

                          {/* {
                            entityComments[index]
                            && Object.keys(entityComments[index]).includes(reqKey) &&
                            <Tooltip
                              id="info-tooltip"
                              placement="right"
                              onIsOpenChange={function Ua() { }}
                            >
                              <TooltipTrigger>
                                <InfoTooltipButton screenReaderLabel="Tell Me Why" />
                              </TooltipTrigger>
                              <TooltipContent>
                                {entityComments[index][reqKey]}
                              </TooltipContent>
                            </Tooltip>
                          } */}
                        </DataTableCellV2>
                      ))
                    }
                    {
                      isRowExpanded.length > 0
                      && isRowExpanded[index]
                      && (
                        <DataTableExpandableContentV2>
                          {
                            getDynamicContent(data)
                          }
                        </DataTableExpandableContentV2>
                      )
                    }
                  </DataTableRowV2>

                ))
              }

            </DataTableBodyV2>
          </DataTableV2>
        )
      }
      {
        tableData.length === 0 && fieldLabel && fieldLabel.labelnorecords
        && (
          <p className={noRecordLabelCss}>{parseHtml(fieldLabel.labelnorecords)}</p>
        )
      }
      {
        tableData.length > 0
        && fieldLabel && fieldLabel.additionalDataMsg
        && (
          <p className={additionalDataMsgCss}>{parseHtml(fieldLabel.additionalDataMsg)}</p>
        )
      }
    </div>
  );
};

export default ExpandableTable;
